// @flow
import React, { useContext, forwardRef } from 'react';
import classnames from 'classnames';
import { isEmpty } from 'lodash';

import ResponsiveContext from 'Components/utils/responsive/responsiveContext';

import DynamicLink from 'Components/frameworks/dynamic-link/DynamicLink.jsx';
import { Scrollable } from 'Components/frameworks/scrollable/Scrollable.jsx';
import { TrackClick } from 'Tracking/track-click/TrackClick.jsx';

import { themes } from './Navbar.themes';

type Props = {
  className?: string,
  getTrackingData?: string => TrackingData,
  id?: string,
  items: Array<{
    label: string,
    link: *,
  }>,
  scrollBackwardLabel?: string,
  scrollForwardLabel?: string,
  selectedItem?: number,
  theme?: string,
};

// We use `forwardRef` to give the parent an access to the <nav> DOM and get its height for example.
export const Navbar = forwardRef<Props, *>((props: Props, ref) => {
  const {
    className,
    id,
    items = [],
    scrollBackwardLabel,
    scrollForwardLabel,
    selectedItem = -1,
    theme = 'default',
    getTrackingData,
  } = props;

  const { isRTL } = useContext(ResponsiveContext);

  if (isEmpty(items) || !Array.isArray(items)) {
    return null;
  }

  const { thList, thItem, thItemActive, thItemInactive, thRoot, thScrollArea } = themes[theme];

  const children = items.map((item, index) => {
    const isActive = index === selectedItem;
    return isEmpty(item) ? null : (
      <TrackClick
        {...item.link}
        as={DynamicLink}
        key={item.label}
        className={classnames(thItem, {
          [thItemInactive]: !isActive,
          [thItemActive]: isActive,
        })}
        trackingData={getTrackingData ? getTrackingData(item.label) : null}
      >
        {item.label}
      </TrackClick>
    );
  });

  return (
    <nav
      className={classnames(className, thRoot)}
      data-testid="navbar"
      id={id || null}
      ref={ref}
      role="navigation"
    >
      <Scrollable
        backwardLabel={scrollBackwardLabel}
        className={thScrollArea}
        forwardLabel={scrollForwardLabel}
        hasShadow
        navPosition="side"
      >
        <div className={thList}>{isRTL ? children.reverse() : children}</div>
      </Scrollable>
    </nav>
  );
});
